import { api } from '../../lib/api';
import { timeAgo } from '../../lib/format';
import { useLiveOrders } from '../../lib/useLiveOrders';
import { useToasts } from '../../lib/toast';
import { useDevice } from '../../store';
import { TopBar } from '../TopBar';
import { LoginGate } from '../LoginGate';
import { ROOMS } from '@taj/types';
import type { OrderDto, OrderStatus } from '@taj/types';

// La cuisine avance jusqu'à « Prêt » seulement. La dernière étape (Prêt → Terminé) est faite
// par le COMPTOIR quand il remet la commande au client → la commande ne disparaît pas du
// comptoir tant qu'elle n'a pas été remise. La colonne « Prêt » n'a donc pas de bouton.
/** Heure demandée par le client, en HH:MM. */
const hhmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const COLS: { status: OrderStatus; label: string; cls: string; adv?: string; advCls?: string }[] = [
  { status: 'RECEIVED', label: 'Reçu', cls: 'received', adv: '▶ Commencer', advCls: 'to-prep' },
  { status: 'PREPARING', label: 'En prépa', cls: 'preparing', adv: '✓ Prêt', advCls: 'to-ready' },
  { status: 'READY', label: 'Prêt', cls: 'ready' },
];

export function Kds() {
  const auth = useDevice((s) => s.auth);
  const station = useDevice((s) => s.station) ?? '';
  const push = useToasts((s) => s.push);
  const { orders, live } = useLiveOrders(ROOMS.kds(station));

  if (!auth) return <LoginGate mode="pin" />;

  // Ne garder que les commandes ayant au moins une ligne pour ce poste.
  const mine = orders.filter((o) => o.lines.some((l) => l.station === station));

  const advance = async (o: OrderDto) => {
    try {
      await api.advanceOrder(o.id);
    } catch (e: any) {
      push(e.message, 'warn');
    }
  };

  const printLabels = async (o: OrderDto) => {
    push('🏷️ Impression des étiquettes…');
    try {
      const r = await api.printLabels(o.id, station);
      push(`✅ ${r.count} étiquette(s) imprimée(s)`);
    } catch (e: any) {
      push(e.message, 'warn');
    }
  };

  const linesFor = (o: OrderDto) => o.lines.filter((l) => l.station === station);

  return (
    <div className="kiosk wide">
      <TopBar
        right={
          <span className="live-pill">
            <span className={`ldot${live ? '' : ' off'}`} />
            {station}
          </span>
        }
      />
      <div className="staff-header">
        <div className="staff-title">👨‍🍳 Cuisine — {station}</div>
        <div className="staff-subtitle">{mine.length} commande(s) active(s)</div>
      </div>
      <div className="k-cols">
        {COLS.map((col) => {
          const inCol = mine.filter((o) => o.status === col.status);
          return (
            <div key={col.status}>
              <div className={`k-col-head stat-pill ${col.status}`}>
                {col.label} ({inCol.length})
              </div>
              {inCol.map((o) => (
                <div key={o.id} className="k-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="k-id">#{o.number}</span>
                    <span style={{ fontSize: '.6rem', color: 'var(--muted)' }}>{timeAgo(o.placedAt)}</span>
                  </div>
                  <div style={{ fontSize: '.62rem', color: 'var(--gold)', fontWeight: 800, marginBottom: 5 }}>
                    {o.type === 'DINE_IN' ? '🪑' : '🛍️'} {o.clientName}
                  </div>
                  {/* Commande demandée pour une heure précise : la cuisine doit le voir avant tout. */}
                  {o.wantedAt && <div className="k-wanted">⏰ POUR {hhmm(o.wantedAt)}</div>}
                  <ul className="cl">
                    {linesFor(o).map((l) => (
                      <li key={l.id}>
                        <b>{l.qty}×</b> {l.nameSnap}
                        {l.options.length > 0 && (
                          <div className="k-opts">+ {l.options.map((op) => op.nameSnap).join(', ')}</div>
                        )}
                        {l.notes && <div className="k-note">📝 {l.notes}</div>}
                      </li>
                    ))}
                  </ul>
                  {col.adv ? (
                    <button className={`k-adv ${col.advCls}`} onClick={() => advance(o)}>
                      {col.adv}
                    </button>
                  ) : (
                    <div style={{ textAlign: 'center', fontSize: '.75rem', color: 'rgba(245,240,232,.5)', padding: '6px 0' }}>
                      🛍️ Remise au comptoir
                    </div>
                  )}
                  <button className="k-label" onClick={() => printLabels(o)}>🏷️ Étiquettes</button>
                </div>
              ))}
              {inCol.length === 0 && (
                <div style={{ textAlign: 'center', padding: '14px 0', color: 'rgba(245,240,232,.12)' }}>—</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
